import { supabase } from '@/integrations/supabase/client';

const isLocalhost = () =>
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

const getSessionToken = async () => (await supabase.auth.getSession()).data.session?.access_token || null;

export interface GoogleCalendarStatus {
  configured: boolean;
  connected: boolean;
  setupPending?: boolean;
  email?: string | null;
  calendarName?: string;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  /** Falha ao consultar a situação (o botão aparece e mostra o motivo) */
  error?: string;
  /** Permissão para ler os e-mails dos tribunais */
  gmailEnabled?: boolean;
  gmailLastSync?: string | null;
  gmailLastResult?: {
    emails: number;
    emailsComProcesso: number;
    atualizados: { numero: string; titulo: string; novos: number }[];
    naoCadastrados: string[];
    erro: string | null;
  } | null;
}

/** Lê agora os e-mails do dia e atualiza os processos citados */
export async function syncEmailsNow() {
  const token = await getSessionToken();
  if (!token) throw new Error('Sessão expirada. Entre novamente no sistema.');
  const response = await fetch('/api/google-calendar/email-sync', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Falha ao ler os e-mails');
  return data as NonNullable<GoogleCalendarStatus['gmailLastResult']>;
}

export async function getGoogleCalendarStatus(): Promise<GoogleCalendarStatus | null> {
  if (isLocalhost()) return null;
  const token = await getSessionToken();
  if (!token) return null;
  try {
    const response = await fetch('/api/google-calendar/status', { headers: { Authorization: `Bearer ${token}` } });
    if (response.status === 503) return { configured: false, connected: false };
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return { configured: true, connected: false, error: data.error || `Erro ${response.status}` };
    }
    return response.json();
  } catch {
    return { configured: true, connected: false, error: 'Sem conexão com o servidor' };
  }
}

export async function connectGoogleCalendar() {
  const token = await getSessionToken();
  if (!token) throw new Error('Sessão expirada. Entre novamente no sistema.');
  window.location.href = `/api/google-calendar/connect?token=${encodeURIComponent(token)}`;
}

export async function syncGoogleCalendarNow(): Promise<boolean> {
  const token = await getSessionToken();
  if (!token) return false;
  const response = await fetch('/api/google-calendar/sync', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Falha ao sincronizar com o Google Agenda');
  }
  return (await response.json()).synced;
}

export async function disconnectGoogleCalendar() {
  const token = await getSessionToken();
  if (!token) return;
  await fetch('/api/google-calendar/disconnect', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
}

let pendingPortal: ReturnType<typeof setTimeout> | null = null;

/** Publica no Portal do Cliente as tarefas concluídas marcadas para o cliente (silencioso) */
export function requestPortalSync() {
  if (isLocalhost()) return;
  if (pendingPortal) clearTimeout(pendingPortal);
  pendingPortal = setTimeout(async () => {
    pendingPortal = null;
    try {
      const token = await getSessionToken();
      if (!token) return;
      await fetch('/api/google-calendar/portal-sync', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } catch (error: any) {
      console.warn('[Portal do Cliente] Publicação adiada:', error?.message);
    }
  }, 1200);
}

let pendingSync: ReturnType<typeof setTimeout> | null = null;

/**
 * Pede ao servidor para enviar ao Google as mudanças recém-salvas.
 * Silencioso: se o Google não estiver conectado, não faz nada.
 * Agrupa várias alterações seguidas em uma única sincronização.
 */
export function requestGoogleCalendarSync() {
  if (isLocalhost()) return;
  if (pendingSync) clearTimeout(pendingSync);
  pendingSync = setTimeout(() => {
    pendingSync = null;
    syncGoogleCalendarNow().catch((error) => console.warn('[Google Agenda] Sincronização adiada:', error.message));
  }, 1500);
}
